import { FunctionComponent, ReactNode } from "react";

import { classNames } from "@/utils/classNames";

import { HelpLauncher } from "@/components/HelpCenter/HelpLauncher";

import { getListBuilderTitle } from "../utils/listBuilderTitle";

interface ListBuilderLayoutProps {
	/** Page title shown in the top header bar (e.g. "Create XLSX List"). */
	title?: ReactNode;
	/**
	 * Translated source-type label (e.g. "Smart List"). When provided, the header builds the
	 * "Create <label> List" title via {@link getListBuilderTitle}, which avoids doubling "List"
	 * for labels that already end in it (e.g. "Create Smart List", not "Create Smart List List").
	 * Use this instead of `title` on the create/edit step pages.
	 */
	sourceTypeLabel?: string;
	/** Step bar + form content rendered below the header, inside the padded body. */
	children: ReactNode;
	/** Override body padding — pages with a fixed bottom action bar need extra bottom space. */
	bodyClassName?: string;
}

/**
 * Shared layout for the list-builder (create/edit) flow. Renders the same
 * full-width header bar used across the app (see `ListOverview` / `ListContextHeader`)
 * and a padded body for the stepper and step content. Replaces the per-page
 * breadcrumb + inline title so the create flow matches every other surface.
 */
export const ListBuilderLayout: FunctionComponent<ListBuilderLayoutProps> = ({
	title,
	sourceTypeLabel,
	children,
	bodyClassName = "w-full p-4 pb-8",
}) => {
	const headerTitle = sourceTypeLabel !== undefined ? getListBuilderTitle(sourceTypeLabel) : title;

	return (
		<div className="sm:block flex flex-col items-start justify-between">
			{/* Sticky header bar: stays pinned to the top of the scroll viewport while the step content
			    scrolls. The Stepper (first child of the body) sticks directly beneath it at top-12 (48px). */}
			<div className="sticky top-0 z-30 flex items-center gap-2.5 h-12 py-2 px-2 bg-white border-b border-border-light w-full">
				<span className="text-sm font-semibold text-text whitespace-nowrap">{headerTitle}</span>

				<div className="ml-auto flex items-center gap-2">
					<HelpLauncher />
				</div>
			</div>

			{/* isolate: the step content carries high z-indexes (Tooltip triggers are z-100, see
			    components/Tooltip) that would otherwise paint over the z-30 sticky header while
			    scrolling behind it (PREC-3748). Isolating the body into its own stacking context
			    keeps that content beneath the header. Mirrors the Feed sticky-nav fix (Feed.tsx). */}
			<div className={classNames("isolate", bodyClassName)}>{children}</div>
		</div>
	);
};
